import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { customerAPI, exportAPI } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { formatDateTime } from "../lib/utils";
import { Plus, Search, User, Phone, Mail, Building, Edit2, Trash2, Download } from "lucide-react";
import { toast } from "sonner";
import CustomerCsvImport from "../components/CustomerCsvImport";

export function Customers() {
  const { canEditCustomers, isAdmin } = useAuth();
  const [customers, setCustomers] = useState([]);
  const [filteredCustomers, setFilteredCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [newCustomer, setNewCustomer] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    address: "",
    pin: "",
    managed_toner_service: false
  });
  const [editCustomer, setEditCustomer] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    address: "",
    pin: "",
    managed_toner_service: false
  });

  const loadCustomers = useCallback(async () => {
    try {
      setLoading(true);
      const response = await customerAPI.getAll();
      setCustomers(response.data);
    } catch (error) {
      toast.error("Failed to load customers");
      console.error("Load customers error:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  const filterCustomers = useCallback(() => {
    const filtered = customers.filter(customer =>
      customer.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      customer.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      customer.company?.toLowerCase().includes(searchTerm.toLowerCase())
    );
    // Sort customers alphabetically by name
    const sorted = filtered.sort((a, b) => a.name.localeCompare(b.name));
    setFilteredCustomers(sorted);
  }, [customers, searchTerm]);

  useEffect(() => {
    loadCustomers();
  }, [loadCustomers]);

  useEffect(() => {
    filterCustomers();
  }, [filterCustomers]);

  const handleCreateCustomer = async (e) => {
    e.preventDefault();
    if (!newCustomer.name.trim()) {
      toast.error("Customer name is required");
      return;
    }

    try {
      await customerAPI.create(newCustomer);
      toast.success("Customer created successfully");
      setShowCreateDialog(false);
      setNewCustomer({
        name: "",
        email: "",
        phone: "",
        company: "",
        address: "",
        pin: "",
        managed_toner_service: false
      });
      loadCustomers();
    } catch (error) {
      toast.error("Failed to create customer");
      console.error("Create customer error:", error);
    }
  };

  const handleEditCustomer = (customer) => {
    setEditingCustomer(customer);
    setEditCustomer({
      name: customer.name,
      email: customer.email || "",
      phone: customer.phone || "",
      company: customer.company || "",
      address: customer.address || "",
      pin: customer.pin || "",
      managed_toner_service: customer.managed_toner_service || false
    });
    setShowEditDialog(true);
  };

  const handleUpdateCustomer = async (e) => {
    e.preventDefault();
    if (!editCustomer.name.trim()) {
      toast.error("Customer name is required");
      return;
    }

    try {
      await customerAPI.update(editingCustomer.id, editCustomer);
      toast.success("Customer updated successfully");
      setShowEditDialog(false);
      setEditingCustomer(null);
      loadCustomers();
    } catch (error) {
      toast.error("Failed to update customer");
      console.error("Update customer error:", error);
    }
  };

  const handleDeleteCustomer = async (customer) => {
    if (!window.confirm(`Are you sure you want to delete "${customer.name}"? This action cannot be undone.`)) {
      return;
    }

    try {
      await customerAPI.delete(customer.id);
      toast.success("Customer deleted successfully");
      loadCustomers();
    } catch (error) {
      toast.error("Failed to delete customer");
      console.error("Delete customer error:", error);
    }
  };

  const handleExportCustomers = async () => {
    try {
      const response = await exportAPI.exportCustomersCSV();
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      a.download = 'customers_export.csv';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success("Customers exported successfully");
    } catch (error) {
      toast.error("Failed to export customers");
      console.error("Export customers error:", error);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 fade-in">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900" data-testid="customers-title">
            Customers
          </h1>
          <p className="text-slate-600 mt-1">Manage your customer database</p>
        </div>
        
        <div className="flex gap-2">
          {isAdmin && (
            <Button
              variant="outline"
              onClick={handleExportCustomers}
              className="btn-hover"
              data-testid="export-customers-button"
            >
              <Download className="w-4 h-4 mr-2" />
              Export CSV
            </Button>
          )}
          
          {canEditCustomers && (
          <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
            <DialogTrigger asChild>
              <Button className="btn-hover" data-testid="add-customer-button">
                <Plus className="w-4 h-4 mr-2" />
                Add Customer
              </Button>
            </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add New Customer</DialogTitle>
              <DialogDescription>
                Enter the customer details below.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleCreateCustomer} className="space-y-4" data-testid="create-customer-form">
              <div>
                <Label htmlFor="name">Name *</Label>
                <Input
                  id="name"
                  value={newCustomer.name}
                  onChange={(e) => setNewCustomer({ ...newCustomer, name: e.target.value })}
                  placeholder="Customer name"
                  required
                  data-testid="customer-name-input"
                />
              </div>
              <div>
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={newCustomer.email}
                  onChange={(e) => setNewCustomer({ ...newCustomer, email: e.target.value })}
                  placeholder="customer@example.com"
                  data-testid="customer-email-input"
                />
              </div>
              <div>
                <Label htmlFor="phone">Phone</Label>
                <Input
                  id="phone"
                  value={newCustomer.phone}
                  onChange={(e) => setNewCustomer({ ...newCustomer, phone: e.target.value })}
                  placeholder="Phone number"
                  data-testid="customer-phone-input"
                />
              </div>
              <div>
                <Label htmlFor="company">Company</Label>
                <Input
                  id="company"
                  value={newCustomer.company}
                  onChange={(e) => setNewCustomer({ ...newCustomer, company: e.target.value })}
                  placeholder="Company name"
                  data-testid="customer-company-input"
                />
              </div>
              <div>
                <Label htmlFor="address">Address</Label>
                <Input
                  id="address"
                  value={newCustomer.address}
                  onChange={(e) => setNewCustomer({ ...newCustomer, address: e.target.value })}
                  placeholder="Address"
                  data-testid="customer-address-input"
                />
              </div>
              <div>
                <Label htmlFor="pin">PIN (for Toner Management Access)</Label>
                <Input
                  id="pin"
                  value={newCustomer.pin}
                  onChange={(e) => setNewCustomer({ ...newCustomer, pin: e.target.value })}
                  placeholder="Enter a PIN for customer access"
                  data-testid="customer-pin-input"
                />
              </div>
              <div className="flex items-center justify-between py-2 border rounded-md px-4">
                <div className="space-y-0.5">
                  <Label htmlFor="managed-toner">Managed Toner Service</Label>
                  <p className="text-sm text-gray-500">Enable monthly toner reminders</p>
                </div>
                <Switch
                  id="managed-toner"
                  checked={newCustomer.managed_toner_service}
                  onCheckedChange={(checked) => setNewCustomer({ ...newCustomer, managed_toner_service: checked })}
                />
              </div>
              <div className="flex space-x-2">
                <Button type="submit" className="flex-1" data-testid="save-customer-button">
                  Create Customer
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowCreateDialog(false)}
                  data-testid="cancel-customer-button"
                >
                  Cancel
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
        )}
        </div>

        {/* Edit Customer Dialog */}
        <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Edit Customer</DialogTitle>
              <DialogDescription>
                Update the customer details below.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleUpdateCustomer} className="space-y-4" data-testid="edit-customer-form">
              <div>
                <Label htmlFor="edit-name">Name *</Label>
                <Input
                  id="edit-name"
                  value={editCustomer.name}
                  onChange={(e) => setEditCustomer({ ...editCustomer, name: e.target.value })}
                  placeholder="Customer name"
                  required
                  data-testid="edit-customer-name-input"
                />
              </div>
              <div>
                <Label htmlFor="edit-email">Email</Label>
                <Input
                  id="edit-email"
                  type="email"
                  value={editCustomer.email}
                  onChange={(e) => setEditCustomer({ ...editCustomer, email: e.target.value })}
                  placeholder="customer@example.com"
                  data-testid="edit-customer-email-input"
                />
              </div>
              <div>
                <Label htmlFor="edit-phone">Phone</Label>
                <Input
                  id="edit-phone"
                  value={editCustomer.phone}
                  onChange={(e) => setEditCustomer({ ...editCustomer, phone: e.target.value })}
                  placeholder="Phone number"
                  data-testid="edit-customer-phone-input"
                />
              </div>
              <div>
                <Label htmlFor="edit-company">Company</Label>
                <Input
                  id="edit-company"
                  value={editCustomer.company}
                  onChange={(e) => setEditCustomer({ ...editCustomer, company: e.target.value })}
                  placeholder="Company name"
                  data-testid="edit-customer-company-input"
                />
              </div>
              <div>
                <Label htmlFor="edit-address">Address</Label>
                <Input
                  id="edit-address"
                  value={editCustomer.address}
                  onChange={(e) => setEditCustomer({ ...editCustomer, address: e.target.value })}
                  placeholder="Address"
                  data-testid="edit-customer-address-input"
                />
              </div>
              <div>
                <Label htmlFor="edit-pin">PIN (for Toner Management Access)</Label>
                <Input
                  id="edit-pin"
                  value={editCustomer.pin}
                  onChange={(e) => setEditCustomer({ ...editCustomer, pin: e.target.value })}
                  placeholder="Enter a PIN for customer access"
                  data-testid="edit-customer-pin-input"
                />
              </div>
              <div className="flex items-center justify-between py-2 border rounded-md px-4">
                <div className="space-y-0.5">
                  <Label htmlFor="edit-managed-toner">Managed Toner Service</Label>
                  <p className="text-sm text-gray-500">Enable monthly toner reminders</p>
                </div>
                <Switch
                  id="edit-managed-toner"
                  checked={editCustomer.managed_toner_service}
                  onCheckedChange={(checked) => setEditCustomer({ ...editCustomer, managed_toner_service: checked })}
                />
              </div>
              <div className="flex space-x-2">
                <Button type="submit" className="flex-1" data-testid="update-customer-button">
                  Update Customer
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowEditDialog(false)}
                  data-testid="cancel-edit-customer-button"
                >
                  Cancel
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* CSV Import Component */}
      <CustomerCsvImport onImportComplete={loadCustomers} />

      {/* Search */}
      <div className="flex items-center space-x-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
          <Input
            placeholder="Search customers..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
            data-testid="search-customers-input"
          />
        </div>
        <div className="text-sm text-slate-600" data-testid="customer-count">
          {filteredCustomers.length} of {customers.length} customers
        </div>
      </div>

      {/* Customers Grid */}
      {filteredCustomers.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="customers-grid">
          {filteredCustomers.map((customer) => (
            <Card key={customer.id} className="card-hover" data-testid={`customer-card-${customer.id}`}>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <Link to={`/customers/${customer.id}`} className="flex items-center space-x-3 flex-1 min-w-0">
                    <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-cyan-600 rounded-full flex items-center justify-center text-white font-semibold text-lg">
                      {customer.name.charAt(0).toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <CardTitle className="text-lg truncate">{customer.name}</CardTitle>
                      {customer.company && (
                        <CardDescription className="truncate">{customer.company}</CardDescription>
                      )}
                    </div>
                  </Link>
                  
                  {canEditCustomers && (
                    <div className="flex space-x-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => {
                          e.preventDefault();
                          handleEditCustomer(customer);
                        }}
                        className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                        data-testid={`edit-customer-${customer.id}`}
                      >
                        <Edit2 className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => {
                          e.preventDefault();
                          handleDeleteCustomer(customer);
                        }}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        data-testid={`delete-customer-${customer.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  )}
                </div>
              </CardHeader>
              <Link to={`/customers/${customer.id}`}>
                <CardContent>
                  <div className="space-y-2">
                    {customer.email && (
                      <div className="flex items-center text-sm text-slate-600">
                        <Mail className="w-4 h-4 mr-2 text-slate-400" />
                        <span className="truncate">{customer.email}</span>
                      </div>
                    )}
                    {customer.phone && (
                      <div className="flex items-center text-sm text-slate-600">
                        <Phone className="w-4 h-4 mr-2 text-slate-400" />
                        <span>{customer.phone}</span>
                      </div>
                    )}
                    <div className="text-xs text-slate-500 pt-2">
                      Added: {formatDateTime(customer.created_date)}
                    </div>
                  </div>
                </CardContent>
              </Link>
            </Card>
          ))}
        </div>
      ) : (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-16">
            <User className="w-16 h-16 text-slate-300 mb-4" />
            <h3 className="text-lg font-medium text-slate-900 mb-2" data-testid="no-customers-title">
              No customers found
            </h3>
            <p className="text-slate-600 text-center mb-6">
              {searchTerm ? "Try adjusting your search criteria" : "Get started by adding your first customer"}
            </p>
            {!searchTerm && canEditCustomers && (
              <Button onClick={() => setShowCreateDialog(true)} data-testid="add-first-customer-button">
                <Plus className="w-4 h-4 mr-2" />
                Add Your First Customer
              </Button>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
